import React from "react";
import useLogin from "@common/components/hooks/useLogin";
import { Controller } from "react-hook-form";
import { Form, Label } from "semantic-ui-react";

/**
 * CheckboxComponent renders a with validation.
 *
 * @typedef {object} InputProps
 * @property {Function} control - Function to control the checkbox field for form
 * @property {string} name - Name of the checkbox field.
 * @property {string} [label=''] - Label of the checkbox field.
 * @property {Object} [error=null] - Object containing validation error.
 * @property {boolean} [multipleCheckboxes=false] - Boolean to determine if there will be multiple checkboxes.
 * @property {array} [multiData=null] - The data to be used with the multiple checkboxes.
 * @property {Function} [boxChecked=null] - A custom way to handle if the checkbox is checked.
 * @property {Function} [handleChange=null] - A custom way to handle the checkbox being clicked.
 * @property {string} [fieldStyle='flex-row-center'] - Style for Form.Field.
 * @property {string} [labelStyle=''] - Style for the label.
 * @property {string} [textareaContainerStyle=''] - Style for checkbox container.
 *
 * @component
 * @param {InputProps} props - The component props.
 * @returns {JSX.Element} The rendered checkbox field with validation.
 */
export default function CheckboxComponent({
  control,
  name,
  label = "",
  error = null,
  multipleCheckboxes = false,
  multiData = null,
  boxChecked = null,
  handleChange = null,
  fieldStyle = "flex-row-center",
  labelStyle,
  checkboxContainerStyle,
}) {
  const [{ isInReadMode }] = useLogin();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <>
          {multipleCheckboxes ? (
            <Form.Field>
              {multiData.map((data) => (
                <Form.Field
                  className={fieldStyle}
                  error={!!error}
                  key={data.value}
                >
                  <div className={checkboxContainerStyle}>
                    <Form.Checkbox
                      readOnly={isInReadMode}
                      checked={
                        boxChecked === null
                          ? value.includes(data.value)
                          : boxChecked
                      }
                      onChange={(_, { checked }) => {
                        if (handleChange !== null) {
                          handleChange();
                        } else {
                          let valueCopy = value;
                          valueCopy.includes(data.value)
                            ? valueCopy.splice(valueCopy.indexOf(data.value), 1)
                            : valueCopy.push(data.value);
                          onChange(valueCopy);
                        }
                      }}
                      className="m-0"
                      label={data.text}
                    />
                  </div>
                </Form.Field>
              ))}
              <Form.Field className="m-0" error={!!error}>
                {error && (
                  <Label pointing="" prompt>
                    {error?.message}
                  </Label>
                )}
              </Form.Field>
            </Form.Field>
          ) : (
            <>
              <Form.Field className={fieldStyle} error={!!error}>
                <div className={checkboxContainerStyle}>
                  <Form.Checkbox
                    readOnly={isInReadMode}
                    checked={boxChecked === null ? value : boxChecked}
                    onChange={(_, { checked }) => {
                      if (handleChange !== null) {
                        onChange(checked);
                        handleChange();
                      } else {
                        onChange(checked);
                      }
                    }}
                    className="m-0"
                    label={label}
                  />
                </div>
              </Form.Field>
              <Form.Field className="m-0" error={!!error}>
                {error && (
                  <Label pointing="" prompt>
                    {error?.message}
                  </Label>
                )}
              </Form.Field>
            </>
          )}
        </>
      )}
    />
  );
}
